import { Box, CircularProgress, Typography, styled } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useController } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useToken } from "../../../server/auth/use-token";
import { ImagesService } from "../../../server/cloudflare/images-service";
import { convertSvgImage } from "../../../utils/convert-svg-image";
import { uuidv4 } from "../../../utils/uuid";
import { useElementTracking } from "../../analytics/analytics";
import { useBrandKit } from "../../brand-kit/hooks/use-brand-kit";
import { FilePicker } from "../../generic/components/file-picker/file-picker";
import { CeAlign } from "../../generic/components/form-fields/ce-align";
import { CeLogoSelect } from "../../generic/components/form-fields/ce-logo-select";
import { CeSlider } from "../../generic/components/form-fields/ce-slider";
import { CeTextInput } from "../../generic/components/form-fields/ce-text-field";
import { FormStack } from "../../generic/components/form-fields/form-stack";

import type { BrandKitLogo } from "../../brand-kit/server/brand-kit-service";
import type { EmailDesignSchema } from "../../email-design/types/email-design-type";
import type { FC } from "react";

const FormSectionHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(18),
  marginBottom: theme.spacing(2),
}));

export const LogoSettingsEditor: FC = () => {
  const { loading, brandKit } = useBrandKit();
  const analyticsPropsBrandKit = useElementTracking("email_design", "logo", "brand_kit");

  console.log({ analyticsPropsBrandKit });

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <FormStack>
      <CeAlign analyticsCategory="email_design" name="header.logo.align" />
      <CeSlider<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.logo.proportionOfMaxWidth"
        label="Size"
        max={1}
        min={0.1}
        step={0.01}
        valueLabelFormat={(val) => `${Math.round(val * 100)}%`}
        defaultValue={1}
      ></CeSlider>
      <CeTextInput<EmailDesignSchema>
        analyticsCategory="email_design"
        name="header.logo.alt"
        label="Alt text"
      />
      {brandKit?.choices.logos && (
        <Box {...analyticsPropsBrandKit}>
          <FormSectionHeader>Brand Kit Logos</FormSectionHeader>
          <CeLogoSelect logos={brandKit.choices.logos} />
        </Box>
      )}
      <HeaderLogoUpload logos={brandKit?.choices.logos} />
    </FormStack>
  );
};

const HeaderLogoUpload: FC<{ logos?: BrandKitLogo[] }> = ({ logos }) => {
  const getToken = useToken();

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<EmailDesignSchema, "header.logo.src">({
    name: "header.logo.src",
  });

  const [src, setSrc] = useState<string>();

  useEffect(() => {
    if (logos) {
      if (logos.some((logo) => logo.src === value)) {
        if (src === undefined) {
          setSrc("");
        }
        return;
      }
    }
    setSrc(value);
  }, [logos, src, value]);

  const handleUpload = useCallback(
    async (file: File) => {
      const name = uuidv4();

      const imagesService = new ImagesService();
      const result = await imagesService.upload(
        file.type.includes("image/svg") ? (await convertSvgImage(file, "email")).file : file,
        name,
        await getToken(),
      );

      if (!result.success || !result.url) {
        toast.error("Something went wrong, please try again");
        return;
      }

      setSrc(result.url);
      onChange(result.url);

      return file;
    },
    [getToken, onChange],
  );

  const selected = src !== undefined && src === value;

  const handleRemove = useCallback(() => {
    setSrc("");
    onChange(null);
  }, [onChange]);

  if (src === undefined) {
    return null;
  }

  return (
    <Box>
      <FormSectionHeader>Custom Logo</FormSectionHeader>
      <FilePicker
        analyticsCategory="email_design"
        formControlProps={{ sx: {} }}
        text={{
          add: "Add Logo",
          remove: "Remove Logo",
          uploadError: "Only one logo can be uploaded",
        }}
        preview={false}
        accept={{ "image/*": [".png", ".jpeg", ".gif", ".jpg"] }}
        onClick={onChange}
        value={src}
        handleUpload={handleUpload}
        handleRemove={handleRemove}
        selected={selected}
        minWidth="auto"
      />
      {error && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Box>
  );
};
